import {
  Message,
  MessageContent,
  MessageResponse,
} from '@/components/ai-elements/message';
import { DashboardPage } from '@/components/app/dashboard-page';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useGetSession } from '@/features/auth';
import { useGetActiveOrganization } from '@/features/organization/api';
import { useUploadImage } from '@/features/upload/api/upload.hook';
import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import {
  type PublicSiteView,
  publicSitePreviewSchema,
  publicSiteViewSchema,
} from '@borradh-workspace/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowUp,
  ExternalLink,
  Globe,
  Loader2,
  Monitor,
  Scan,
  SlidersHorizontal,
  Smartphone,
  Sparkles,
  Tablet,
  X,
} from 'lucide-react';
import {
  AnimatePresence,
  LayoutGroup,
  motion,
  useReducedMotion,
} from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  acknowledgeDesignerHandoff,
  claimDesignerHandoff,
} from './designer-handoff';
import { openSiteViewer } from './open-site-viewer';
import type { PublicSiteImageInput } from './public-site-update.payload';
import { SiteAgentActivity } from './site-agent-activity';
import { type SiteAppearance, appearancePreview } from './site-appearance';
import { SiteDomainControls } from './site-domain-controls';
import { SiteEditControls } from './site-edit-controls';
import {
  SiteImageAttachments,
  type WebsitePhoto,
} from './site-image-attachments';
import { SitePreview, type SiteSelection } from './site-preview';
import { siteViewerUrl } from './site-viewer-url';
import { usePublicSiteUpdate } from './use-public-site-update';
import { useSiteStream } from './use-site-stream';
import './public-site-builder.css';

const selectionLabels: Record<string, string> = {
  h1: 'Heading',
  h2: 'Heading',
  h3: 'Heading',
  h4: 'Heading',
  p: 'Text',
  img: 'Image',
  a: 'Link',
  button: 'Button',
  li: 'Item',
  section: 'Section',
  header: 'Header',
  footer: 'Footer',
};

export function PublicSiteBuilder({
  locationId,
  handoffToken,
}: { locationId: string; handoffToken?: string }) {
  const reduceMotion = useReducedMotion();
  const { data: organization } = useGetActiveOrganization();
  const { user } = useGetSession();
  const canManage = user?.orgRole === 'owner' || user?.orgRole === 'admin';
  const queryClient = useQueryClient();
  const queryKey = queryKeys.publicSite.detail(organization?.id, locationId);
  const path = `public-sites/${encodeURIComponent(locationId)}`;
  const [prompt, setPrompt] = useState('');
  const captureAttachment = useRef<{
    screenshot: string;
    image: PublicSiteImageInput;
  } | null>(null);
  const captureUpload = useUploadImage({
    purpose: 'profile',
    showToast: false,
  });
  const [photos, setPhotos] = useState<WebsitePhoto[]>([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>(
    'desktop'
  );
  const [selecting, setSelecting] = useState(false);
  const [selection, setSelection] = useState<{
    versionId?: string;
    target: SiteSelection;
  } | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [appearanceDraft, setAppearanceDraft] = useState<{
    versionId?: string;
    value: SiteAppearance | null;
  } | null>(null);
  // Retain a request's key if the HTTP response is lost; a second click safely replays it.
  const transcript = useRef<HTMLDivElement>(null);
  const followConversation = useRef(true);
  const userScrollUntil = useRef(0);
  const attemptedHandoff = useRef<string | null>(null);
  const operation = useRef<{
    prompt: string;
    id: string;
    images?: string;
  } | null>(null);
  const siteQuery = useQuery({
    queryKey,
    queryFn: () =>
      apiClient.get<PublicSiteView>(path, { schema: publicSiteViewSchema }),
    enabled: Boolean(organization?.id),
    refetchOnWindowFocus: true,
  });
  const site = siteQuery.data;
  const stream = useSiteStream(
    path,
    site?.activeOperationId || null,
    site?.status === 'generating'
  );
  const liveContentLength = Object.values(stream.drafts).reduce(
    (total, draft) => total + draft.text.length,
    stream.activities.length
  );
  useEffect(() => {
    if (
      !site?.messages.length &&
      !liveContentLength &&
      site?.status !== 'generating'
    )
      return;
    if (followConversation.current && transcript.current)
      transcript.current.scrollTop = transcript.current.scrollHeight;
  }, [site?.messages.length, site?.status, liveContentLength]);
  useEffect(() => {
    if (siteQuery.isPending || !transcript.current || !window.ResizeObserver)
      return;
    const element = transcript.current;
    const observer = new ResizeObserver(() => {
      if (followConversation.current) element.scrollTop = element.scrollHeight;
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [siteQuery.isPending]);
  const update = usePublicSiteUpdate(locationId, {
    onSuccess: (value, variables) => {
      queryClient.setQueryData(queryKey, value);
      if (variables.action === 'changes') {
        if (handoffToken === variables.operationKey && organization?.id)
          acknowledgeDesignerHandoff(handoffToken, organization.id, locationId);
        setPrompt('');
        setPhotos([]);
        captureAttachment.current = null;
        setSelection(null);
        setSelecting(false);
        operation.current = null;
        setSelectedVersion('');
      }
      if (
        variables.action === 'publish' &&
        !value.publishing &&
        !value.publishError
      )
        toast.success('Your website is now published.');
    },
    onError: (error: Error, variables) => {
      if (variables.action !== 'sync') toast.error(error.message);
    },
  });
  useEffect(() => {
    if ((site?.status !== 'generating' && !site?.publishing) || !canManage)
      return;
    const timer = window.setTimeout(() => {
      if (!update.isPending) update.sync();
    }, 4000);
    return () => window.clearTimeout(timer);
  }, [site, canManage, update.isPending, update.sync]);
  const preview = useQuery({
    queryKey: queryKeys.publicSite.preview(
      organization?.id,
      locationId,
      selectedVersion
    ),
    queryFn: () =>
      apiClient.get<{ html: string }>(
        `${path}/versions/${encodeURIComponent(selectedVersion)}`,
        { schema: publicSitePreviewSchema }
      ),
    enabled: Boolean(selectedVersion),
  });
  const latest = site?.versions.at(-1);
  const versionId = selectedVersion || latest?.id;
  const selectArea = useCallback(
    (target: SiteSelection) => {
      setSelection({ versionId, target });
      setSelecting(false);
    },
    [versionId]
  );
  const cancelCapture = useCallback(() => setSelecting(false), []);
  const html = selectedVersion ? preview.data?.html : site?.html;
  const appearance =
    !selectedVersion &&
    appearanceDraft &&
    appearanceDraft.versionId === versionId
      ? appearanceDraft.value
      : null;
  const viewerUrl =
    html && versionId ? siteViewerUrl(locationId, versionId, appearance) : null;
  function setAppearance(value: SiteAppearance | null) {
    setAppearanceDraft({ versionId, value });
  }
  const busy =
    Boolean(site?.publishingVersionId) ||
    site?.publishing ||
    site?.status === 'generating' ||
    site?.status === 'interrupted' ||
    update.isPending;
  const designing =
    site?.status === 'generating' ||
    (update.isPending && update.variables?.action === 'changes');
  useEffect(() => {
    if (
      !handoffToken ||
      attemptedHandoff.current === handoffToken ||
      !organization?.id ||
      !canManage ||
      !site?.configured ||
      busy
    )
      return;
    const approved = claimDesignerHandoff(
      handoffToken,
      organization.id,
      locationId
    );
    if (!approved) return;
    // Attempt automatically once per mount. On failure retain the approved prompt
    // for manual Send or a remount retry, rather than loop paid admissions.
    attemptedHandoff.current = handoffToken;
    operation.current = { prompt: approved.request, id: approved.operationId };
    setPrompt(approved.request);
    followConversation.current = true;
    update.change(approved.request, approved.operationId);
  }, [
    handoffToken,
    organization?.id,
    locationId,
    canManage,
    site?.configured,
    busy,
    update.change,
  ]);
  async function submit(detail?: string) {
    const selected =
      selection && selection.versionId === versionId ? selection.target : null;
    const text =
      detail?.trim() ||
      (selected
        ? `${prompt.trim()}\n\nSelected website element (version ${versionId}): ${JSON.stringify({ region: selected.region, tag: selected.tag, text: selected.text, selector: selected.selector, html: selected.html.slice(0, 1000) }).slice(0, 2400)}\nApply the request to this element and preserve the rest of the website.`
        : prompt.trim() ||
          (photos.length ? 'Use these photos to improve the website.' : ''));
    if (!text || busy || uploadingPhotos || !site?.configured || !canManage)
      return;
    const images: PublicSiteImageInput[] = photos.map(({ key, name }) => ({
      key,
      name,
    }));
    if (
      selected?.screenshot &&
      captureAttachment.current?.screenshot === selected.screenshot
    ) {
      images.push(captureAttachment.current.image);
    } else if (selected?.screenshot) {
      if (images.length >= 8) {
        toast.error('Remove a photo to make room for this capture.');
        return;
      }
      setUploadingPhotos(true);
      try {
        const bytes = Uint8Array.from(
          atob(selected.screenshot.split(',')[1]),
          (character) => character.charCodeAt(0)
        );
        const result = await captureUpload.uploadAsync(
          new File([bytes], 'Website reference snapshot.png', {
            type: 'image/png',
          })
        );
        if (!result.isPublic) throw new Error('Capture upload unavailable');
        const image: PublicSiteImageInput = {
          key: result.key,
          name: 'Website reference snapshot.png',
          role: 'reference',
        };
        captureAttachment.current = { screenshot: selected.screenshot, image };
        images.push(image);
      } catch {
        toast.warning(
          'Image attachment unavailable. The captured area context will still be sent.'
        );
      } finally {
        setUploadingPhotos(false);
      }
    }
    const signature = JSON.stringify(images);
    if (
      operation.current?.prompt !== text ||
      operation.current.images !== signature
    )
      operation.current = {
        prompt: text,
        id: crypto.randomUUID(),
        images: signature,
      };
    followConversation.current = true;
    update.change(text, operation.current.id, images);
    setEditOpen(false);
  }
  return (
    <>
      <title>Claire Designer | Borradh</title>
      <DashboardPage
        data-claire-designer=""
        title="Claire Designer"
        description="Your website, by conversation."
        width="wide"
        actions={
          <div className="flex items-center gap-2">
            {site && canManage && (
              <SiteDomainControls
                locationId={locationId}
                site={site}
                disabled={busy}
                onUpdated={(updated) =>
                  queryClient.setQueryData(queryKey, updated)
                }
              />
            )}
            {viewerUrl ? (
              <Button variant="outline" asChild>
                <a
                  href={viewerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={openSiteViewer}
                  onAuxClick={openSiteViewer}
                >
                  <ExternalLink className="size-4" />
                  View
                </a>
              </Button>
            ) : (
              <Button variant="outline" disabled>
                <ExternalLink className="size-4" /> View
              </Button>
            )}
            <Button
              disabled={
                !canManage ||
                site?.status === 'generating' ||
                site?.publishing ||
                site?.hostingConfigured === false ||
                Boolean(appearance) ||
                !versionId ||
                update.isPending ||
                (!site?.publishError &&
                  Boolean(versionId) &&
                  site?.publishedVersionId === versionId)
              }
              onClick={() =>
                versionId &&
                update.publish(site?.publishingVersionId || versionId)
              }
            >
              {site?.publishing
                ? 'Publishing…'
                : site?.publishError
                  ? 'Retry publish'
                  : Boolean(versionId) && site?.publishedVersionId === versionId
                    ? 'Published'
                    : selectedVersion && selectedVersion !== latest?.id
                      ? 'Restore & publish'
                      : 'Publish website'}
            </Button>
          </div>
        }
      >
        {siteQuery.isError ? (
          <div role="alert" className="rounded-lg border p-6">
            <p>{siteQuery.error.message}</p>
            <Button variant="outline" onClick={() => void siteQuery.refetch()}>
              Try again
            </Button>
          </div>
        ) : siteQuery.isPending ? (
          <div role="status" className="flex items-center gap-2 p-8">
            <Loader2 className="size-4 animate-spin" />
            Loading your website…
          </div>
        ) : (
          <>
            {!site?.configured && (
              <p
                role="status"
                className="mb-4 rounded-lg border bg-muted/50 p-4 text-sm"
              >
                Website creation is not connected yet. Your saved website
                remains available.
              </p>
            )}
            {!canManage && (
              <p className="mb-4 text-sm text-muted-foreground">
                An owner or admin can edit and publish this website.
              </p>
            )}
            <div className="site-builder grid gap-4 lg:grid-cols-[340px_minmax(0,1fr)] xl:grid-cols-[360px_minmax(0,1fr)]">
              <section
                aria-label="Website conversation"
                className="site-chat flex min-h-0 flex-col overflow-hidden rounded-2xl border border-stone-200/70 bg-white"
              >
                <div className="claire-chat-header flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-2.5">
                    <span className="claire-mark flex size-7 items-center justify-center rounded-lg">
                      <Sparkles className="size-3.5" />
                    </span>
                    <span className="text-[13px] font-medium tracking-tight">
                      Claire Designer
                    </span>
                  </div>
                </div>
                <div
                  ref={transcript}
                  className="claire-transcript min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-5"
                  role="log"
                  aria-label="Design conversation"
                  aria-live="polite"
                  onWheel={() => {
                    userScrollUntil.current = performance.now() + 1000;
                  }}
                  onPointerDown={() => {
                    userScrollUntil.current = performance.now() + 1000;
                  }}
                  onKeyDown={() => {
                    userScrollUntil.current = performance.now() + 1000;
                  }}
                  onScroll={(event) => {
                    if (performance.now() > userScrollUntil.current) return;
                    const element = event.currentTarget;
                    followConversation.current =
                      element.scrollHeight -
                        element.clientHeight -
                        element.scrollTop <
                      80;
                  }}
                >
                  {!site?.messages.length && (
                    <div className="claire-welcome flex h-full flex-col justify-center gap-5 py-8 text-sm">
                      <div className="claire-welcome-art" aria-hidden="true">
                        <span className="claire-art-card claire-art-card-back" />
                        <span className="claire-art-card claire-art-card-front">
                          <Sparkles />
                        </span>
                      </div>
                      <div>
                        <p className="claire-welcome-title mb-3 text-stone-800">
                          A little idea. A beautiful website.
                        </p>
                        <p className="text-[13px] leading-relaxed text-stone-400">
                          A fresh look. A small tweak. Just tell Claire.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="h-auto justify-start whitespace-normal rounded-xl border-stone-200 py-3 text-left text-xs"
                        disabled={!canManage || !site?.configured}
                        onClick={() =>
                          setPrompt(
                            `Make a beautiful website for ${organization?.name || 'our business'}.`
                          )
                        }
                      >
                        Create a welcoming website
                      </Button>
                    </div>
                  )}
                  {site?.messages.map((message) => (
                    <Message
                      from={message.role}
                      key={message.id}
                      className={`site-message text-[13px] leading-relaxed ${message.role === 'user' ? 'whitespace-pre-wrap' : ''}`}
                    >
                      <MessageContent className="text-[13px] leading-relaxed">
                        {message.role !== 'user' && (
                          <p className="mb-2 flex items-center gap-1.5 text-[11px] font-medium text-stone-400">
                            <Sparkles className="size-3 text-violet-400" />
                            Claire
                          </p>
                        )}
                        {message.role === 'assistant' ? (
                          <MessageResponse isAnimating={false}>
                            {message.content ===
                            'Your updated website is ready to preview. Publish it when you are happy with it.'
                              ? 'Your changes are ready. Take a look at the preview.'
                              : message.content}
                          </MessageResponse>
                        ) : message.role === 'user' &&
                          message.content.includes(
                            '\n\nSelected website element (version '
                          ) ? (
                          message.content.split(
                            '\n\nSelected website element (version '
                          )[0]
                        ) : message.content.length > 280 &&
                          message.role === 'user' &&
                          !message.content.startsWith('For the ') &&
                          !message.content.startsWith(
                            'Save this appearance only:'
                          ) &&
                          !message.content.startsWith(
                            'Art-direct all photography properly:'
                          ) ? (
                          <details className="site-brief">
                            <summary className="cursor-pointer text-xs leading-relaxed">
                              {message.content.slice(0, 160)}…{' '}
                              <span className="mt-2 block opacity-60">
                                Read the full brief
                              </span>
                            </summary>
                            <p className="mt-3 text-xs">{message.content}</p>
                          </details>
                        ) : message.content.startsWith(
                            'Save this appearance only:'
                          ) ? (
                          'Save my new style.'
                        ) : message.content.startsWith(
                            'Art-direct all photography properly:'
                          ) ? (
                          'Crop the photos and make them feel editorial.'
                        ) : message.content.startsWith(
                            'For the img with src'
                          ) ? (
                          'Refine the selected image and its crop.'
                        ) : /^For the (h\d|p|a|button) whose exact text/.test(
                            message.content
                          ) ? (
                          'Update the selected text.'
                        ) : (
                          message.content
                        )}
                      </MessageContent>
                    </Message>
                  ))}
                  {designing && (
                    <div className="space-y-4">
                      <SiteAgentActivity
                        activities={stream.activities}
                        connected={stream.connected}
                      />
                      {Object.entries(stream.drafts).map(
                        ([id, draft]) =>
                          draft.text && (
                            <Message key={id} from="assistant">
                              <MessageContent className="text-[13px] leading-relaxed">
                                <MessageResponse isAnimating>
                                  {draft.text}
                                </MessageResponse>
                                <span
                                  aria-hidden="true"
                                  className="ml-1 inline-block h-3 w-0.5 bg-primary motion-safe:animate-pulse"
                                />
                              </MessageContent>
                            </Message>
                          )
                      )}
                    </div>
                  )}
                  {site?.publishError && (
                    <p role="alert" className="text-sm text-destructive">
                      {site.publishError}
                    </p>
                  )}
                  {site?.error && (
                    <div role="alert" className="space-y-2 text-sm">
                      <p>{site.error}</p>
                      {site.status === 'interrupted' && canManage && (
                        <Button
                          variant="outline"
                          disabled={update.isPending}
                          onClick={() => update.retry()}
                        >
                          Resume change
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                {canManage && !site?.messages.length && (
                  <div className="flex flex-wrap gap-1.5 px-5 pb-3">
                    {['More editorial', 'Bigger images', 'Softer colours'].map(
                      (idea) => (
                        <Button
                          variant="outline"
                          size="sm"
                          key={idea}
                          type="button"
                          disabled={busy || !site?.configured}
                          className="site-idea rounded-full border border-stone-200 px-2.5 py-1.5 text-[11px] text-stone-500 disabled:opacity-40"
                          onClick={() => setPrompt(`${idea}, please.`)}
                        >
                          {idea}
                        </Button>
                      )
                    )}
                  </div>
                )}
                <form
                  className="claire-composer mx-3 mb-3 space-y-2 rounded-2xl border border-stone-200/80 bg-white p-3"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void submit();
                  }}
                >
                  {selection && selection.versionId === versionId && (
                    <div className="flex items-center gap-2 rounded-lg bg-violet-50/70 px-2 py-1 text-[11px] text-violet-600">
                      {selection.target.screenshot ? (
                        <img
                          src={selection.target.screenshot}
                          alt="Captured website area"
                          className="h-12 w-20 shrink-0 rounded-md border object-cover"
                        />
                      ) : (
                        <Scan className="size-3.5 shrink-0" />
                      )}
                      <span className="min-w-0 flex-1 truncate">
                        {selection.target.tag === 'region'
                          ? 'Captured area'
                          : selectionLabels[selection.target.tag] ||
                            'Element'}{' '}
                        · {selection.target.text || 'Selected section'}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Remove selected context"
                        onClick={() => setSelection(null)}
                      >
                        <X className="size-3.5" />
                      </Button>
                    </div>
                  )}
                  <label htmlFor="website-prompt" className="sr-only">
                    Describe your website changes
                  </label>
                  <Textarea
                    id="website-prompt"
                    value={prompt}
                    onChange={(event) => setPrompt(event.target.value)}
                    className="claire-input min-h-[84px] resize-none border-0 bg-transparent px-1 py-2 text-[13px] shadow-none focus-visible:ring-0"
                    placeholder="Ask Claire to design or refine…"
                    onKeyDown={(event) => {
                      if (
                        event.key === 'Enter' &&
                        !event.shiftKey &&
                        !event.nativeEvent.isComposing
                      ) {
                        event.preventDefault();
                        if (prompt.trim() || photos.length) void submit();
                      }
                    }}
                    maxLength={5000}
                    disabled={!canManage || busy || !site?.configured}
                    rows={3}
                  />
                  {canManage && site?.imageUploadsEnabled && (
                    <SiteImageAttachments
                      photos={photos}
                      onChange={setPhotos}
                      uploading={uploadingPhotos}
                      onUploading={setUploadingPhotos}
                      disabled={busy || !site?.configured}
                    />
                  )}
                  <div className="flex items-center justify-between gap-3 pt-1">
                    <span className="flex items-center gap-1.5 text-[10px] text-stone-400">
                      <Sparkles className="size-3" />
                      Claire Designer
                    </span>
                    <Button
                      type="submit"
                      size="icon"
                      className="claire-send size-8 rounded-lg bg-stone-800 text-white hover:bg-stone-700 disabled:opacity-25"
                      disabled={
                        (!prompt.trim() && !photos.length) ||
                        uploadingPhotos ||
                        busy ||
                        !site?.configured ||
                        !canManage
                      }
                    >
                      {busy ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <ArrowUp className="size-4" />
                      )}
                      <span className="sr-only">
                        {latest ? 'Update website' : 'Create website'}
                      </span>
                    </Button>
                  </div>
                </form>
              </section>
              <section
                aria-label="Website preview"
                className="site-stage flex min-w-0 flex-col overflow-hidden rounded-2xl border border-stone-200/80 shadow-sm"
              >
                <div className="claire-toolbar flex flex-wrap items-center justify-between gap-2 border-b bg-card p-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <LayoutGroup id="claire-preview-device">
                      <ToggleGroup
                        type="single"
                        value={device}
                        onValueChange={(value) => {
                          if (value) setDevice(value as typeof device);
                        }}
                        aria-label="Preview device"
                        className="claire-devices flex items-center gap-1 rounded-lg bg-stone-100 p-1"
                      >
                        {(
                          [
                            {
                              id: 'desktop',
                              label: 'Desktop preview',
                              Icon: Monitor,
                            },
                            {
                              id: 'tablet',
                              label: 'Tablet preview',
                              Icon: Tablet,
                            },
                            {
                              id: 'mobile',
                              label: 'Mobile preview',
                              Icon: Smartphone,
                            },
                          ] as const
                        ).map(({ id, label, Icon }) => (
                          <ToggleGroupItem
                            key={id}
                            value={id}
                            className={
                              device === id
                                ? 'size-9 bg-background text-primary shadow-sm hover:bg-background'
                                : ''
                            }
                            aria-label={label}
                            aria-pressed={device === id}
                          >
                            {device === id && (
                              <motion.span
                                layoutId="active-device"
                                className="claire-device-active"
                                transition={
                                  reduceMotion
                                    ? { duration: 0 }
                                    : {
                                        type: 'spring',
                                        stiffness: 450,
                                        damping: 34,
                                      }
                                }
                                aria-hidden="true"
                              />
                            )}
                            <Icon className="relative z-10 size-4" />
                          </ToggleGroupItem>
                        ))}
                      </ToggleGroup>
                    </LayoutGroup>
                    {canManage && (
                      <div className="claire-tools flex items-center gap-1 rounded-lg border bg-white p-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className={
                            editOpen
                              ? 'bg-violet-100 text-violet-700 hover:bg-violet-100'
                              : ''
                          }
                          aria-pressed={editOpen}
                          disabled={
                            !html ||
                            busy ||
                            !site?.configured ||
                            Boolean(selectedVersion)
                          }
                          onClick={() => setEditOpen((value) => !value)}
                        >
                          <SlidersHorizontal className="size-4" />
                          Edit details
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className={
                            selecting
                              ? 'bg-violet-100 text-violet-700 hover:bg-violet-100'
                              : ''
                          }
                          aria-pressed={selecting && !busy && !selectedVersion}
                          disabled={
                            !html ||
                            busy ||
                            !site?.configured ||
                            Boolean(selectedVersion)
                          }
                          onClick={() => setSelecting((value) => !value)}
                        >
                          <Scan className="size-4" />
                          Capture area
                        </Button>
                      </div>
                    )}
                  </div>
                  <Select
                    value={selectedVersion || 'latest-draft'}
                    onValueChange={(value) => {
                      setSelectedVersion(value === 'latest-draft' ? '' : value);
                      setSelecting(false);
                      setSelection(null);
                    }}
                    disabled={!latest}
                  >
                    <SelectTrigger
                      aria-label="Website version"
                      className="claire-version w-[180px] bg-background"
                    >
                      <SelectValue>
                        {selectedVersion
                          ? `Version ${(site?.versions.findIndex((version) => version.id === selectedVersion) ?? -1) + 1}`
                          : 'Latest draft'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent
                      align="end"
                      className="w-[320px] max-w-[calc(100vw-24px)]"
                    >
                      <SelectItem value="latest-draft">Latest draft</SelectItem>
                      {site?.versions
                        .slice()
                        .reverse()
                        .map((version, index) => (
                          <SelectItem
                            key={version.id}
                            value={version.id}
                            textValue={`Version ${site.versions.length - index}`}
                          >
                            <span className="flex min-w-0 flex-col gap-1 py-1">
                              <span className="flex items-center gap-2 font-medium">
                                Version {site.versions.length - index}
                                {site.publishedVersionId === version.id && (
                                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-normal text-muted-foreground">
                                    Published
                                  </span>
                                )}
                              </span>
                              <span className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                                {version.label.startsWith(
                                  'Save this appearance only:'
                                )
                                  ? 'Style update'
                                  : version.label.startsWith(
                                        'Art-direct all photography properly:'
                                      )
                                    ? 'Editorial photos'
                                    : version.label.startsWith('For the img')
                                      ? 'Image update'
                                      : version.label.startsWith('For the ')
                                        ? 'Text update'
                                        : version.label.split(
                                            '\n\nSelected website element (version '
                                          )[0]}
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <AnimatePresence initial={false}>
                  {editOpen && html && (
                    <motion.div
                      key="details"
                      className="claire-detail-reveal"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{
                        duration: reduceMotion ? 0 : 0.24,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                    >
                      <SiteEditControls
                        key={versionId}
                        html={html}
                        palette={site?.palette}
                        appearance={appearance}
                        onPreviewAppearance={setAppearance}
                        disabled={busy || !canManage || !site?.configured}
                        onApply={(value) => {
                          void submit(value);
                        }}
                        onClose={() => setEditOpen(false)}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
                <div
                  className={`site-canvas relative flex flex-1 justify-center p-4 lg:p-6 ${site?.status === 'generating' ? 'site-generating' : ''}`}
                >
                  {selecting && (
                    <div className="pointer-events-none absolute top-6 z-10 rounded-full bg-stone-900/90 px-3 py-1.5 text-xs text-white">
                      Drag to capture an area · Esc to cancel
                    </div>
                  )}
                  {html ? (
                    <SitePreview
                      html={appearancePreview(html, appearance)}
                      device={device}
                      selecting={selecting && !busy && !selectedVersion}
                      onCancel={cancelCapture}
                      onSelect={selectArea}
                    />
                  ) : (
                    <div className="claire-preview-empty flex flex-col items-center justify-center gap-3 p-8 text-center text-muted-foreground">
                      {site?.status === 'generating' ? (
                        <div className="site-building w-full max-w-md rounded-2xl border border-white bg-white/70 p-6 text-left shadow-sm">
                          <div className="mb-6 flex items-center gap-2 text-violet-600">
                            <Sparkles className="size-4" />
                            <span className="text-xs font-medium">
                              A little magic is happening
                            </span>
                          </div>
                          <div className="site-shimmer mb-3 h-7 w-4/5 rounded-lg" />
                          <div className="site-shimmer mb-6 h-7 w-3/5 rounded-lg" />
                          <div className="site-shimmer h-48 rounded-xl" />
                          <div className="mt-5 grid grid-cols-3 gap-3">
                            {[1, 2, 3].map((item) => (
                              <div
                                key={item}
                                className="site-shimmer h-16 rounded-lg"
                              />
                            ))}
                          </div>
                        </div>
                      ) : (
                        <Globe className="size-10" />
                      )}
                      <p>
                        {selectedVersion && preview.isError
                          ? 'This preview could not be loaded.'
                          : selectedVersion
                            ? 'Loading preview…'
                            : site?.status === 'generating'
                              ? 'Designing your website…'
                              : 'Your website preview will appear here.'}
                      </p>
                      <p className="text-sm">
                        Your live website changes only when you publish.
                      </p>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </>
        )}
      </DashboardPage>
    </>
  );
}
